<template>
    <purchase-contract-section>
        <template #title>
            <div class="flex w-full items-center justify-between">
                <div class="flex flex-col gap-1 md:flex-row">
                    <div>{{ t('purchaseContract.selectParticipants') }}</div>
                    <div class="font-normal">
                        <span class="hidden lg:inline">- </span
                        >{{ activeFacility.shortName }}
                    </div>
                </div>
                <div>
                    <prime-button
                        :class="[
                            contractData !== undefined && !isActiveSection
                                ? ''
                                : 'invisible'
                        ]"
                        @click="emit('editSection')"
                    >
                        <div class="flex items-center gap-2">
                            <i class="pi pi-pencil" />
                            <span class="lg:hidden">{{ t('edit') }}</span>
                            <span class="hidden lg:inline">{{
                                t('purchaseContract.changeOrder')
                            }}</span>
                        </div>
                    </prime-button>
                </div>
            </div>
        </template>

        <div
            v-if="!isActiveSection && contractData !== undefined"
            class="flex flex-wrap gap-x-16 gap-y-6 py-4"
        >
            <div>
                <label class="font-semibold">{{ t('startDate') }}</label>
                <div>
                    <client-only>
                        {{
                            formatTimelessDate(
                                parseLocal(contractData.startDateLocal)
                            )
                        }}
                    </client-only>
                </div>
            </div>

            <div>
                <label class="font-semibold">{{ t('endDate') }}</label>
                <div
                    v-if="formattedEndDate === null"
                    class="text-gray-400"
                >
                    &mdash;
                </div>
                <div v-else>
                    <client-only>
                        {{ formattedEndDate }}
                    </client-only>
                </div>
            </div>

            <div v-if="isRecurring">
                <label class="font-semibold">{{
                    t('contracts.nextBillDate')
                }}</label>
                <div
                    v-if="formattedNextBillDate === null"
                    class="text-gray-400"
                >
                    &mdash;
                </div>
                <div v-else>
                    <client-only>
                        {{ formattedNextBillDate }}
                    </client-only>
                </div>
            </div>

            <div>
                <label class="font-semibold">{{
                    t('purchaseContract.participants')
                }}</label>
                <div
                    v-if="contractData.enrollmentSpecifications.length === 0"
                    class="text-gray-400"
                >
                    &mdash;
                </div>
                <ul class="list-disc pl-6">
                    <li
                        v-for="enrollmentSpecification in contractData.enrollmentSpecifications"
                        :key="enrollmentSpecification.enrollmentType.id"
                    >
                        {{ enrollmentSpecification.count }}
                        {{ enrollmentSpecification.enrollmentType.singular }}
                    </li>
                </ul>
            </div>

            <div v-if="contractData.promotions.length > 0">
                <label class="font-semibold">{{
                    t('purchaseContract.promotionalCodes')
                }}</label>
                <div class="flex flex-wrap gap-2">
                    <prime-message
                        v-for="promotion in contractData.promotions"
                        :key="promotion.code"
                        severity="success"
                        :closable="false"
                        icon="pi pi-tag"
                        class="!my-0"
                    >
                        <span class="font-semibold">{{ promotion.code }}</span>
                        <template
                            v-if="
                                promotion.promotionCode?.promotion
                                    .translatedDescription ?? ''
                            "
                        >
                            <br />
                            {{
                                promotion.promotionCode?.promotion
                                    .translatedDescription ?? ''
                            }}
                        </template>
                    </prime-message>
                </div>
            </div>
        </div>

        <div v-if="isActiveSection">
            <div class="flex flex-col gap-8 xl:flex-row">
                <div class="flex flex-wrap gap-8 xl:inline xl:space-y-4">
                    <enrollment-type-selectors
                        v-model:enrollment-input="enrollmentInput"
                    />
                </div>

                <div class="flex-1 space-y-4">
                    <div
                        class="flex flex-col items-start gap-4 md:flex-row md:gap-12 lg:gap-16"
                    >
                        <session-join-dates-picker
                            v-model:contract-start-date="contractStartDate"
                            v-model:contract-end-date="contractEndDate"
                            :plan="planData"
                        />

                        <promotional-code
                            v-model="appliedPromotionalCodes"
                            :plan-id="planData.id"
                            :start-date="contractStartDate"
                        />
                    </div>

                    <prime-message
                        v-if="
                            continueAttempts > 0 && startDateErrorMessage !== ''
                        "
                        severity="error"
                        :closable="false"
                    >
                        {{ startDateErrorMessage }}
                    </prime-message>

                    <prime-message
                        v-else-if="
                            continueAttempts > 0 &&
                            bookingLimitsErrorMessage !== null
                        "
                        severity="error"
                        :closable="false"
                    >
                        {{ bookingLimitsErrorMessage }}
                    </prime-message>

                    <template v-else-if="bookingLimitsErrorMessage === null">
                        <dues-pricing-display
                            v-if="isRecurring"
                            :dues-pricing="duesPricing"
                            :billing-interval="planData.terms.billingInterval"
                            :billing-cycle-string="
                                planData.terms.billingCycleString
                            "
                            :next-bill-date="nextBillDate"
                        />

                        <preview-contract-pricing-table
                            :is-loading="isFetching"
                            :cart="cart"
                            :payment-due-mode="paymentDueMode"
                            :deposit-amount="depositAmount"
                        />
                    </template>
                </div>
            </div>

            <div class="mt-6 flex justify-end gap-4">
                <prime-button
                    v-if="contractData === undefined"
                    :label="t('continue')"
                    :loading="isMutating"
                    :disabled="isFetching"
                    @click="handleContinue"
                />

                <prime-button
                    v-else
                    :label="t('continue')"
                    :loading="isMutating"
                    :disabled="isFetching"
                    @click="handleSaveChanges"
                />
            </div>
        </div>
    </purchase-contract-section>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const SelectParticipantsContractFragment = graphql(/** GraphQL */ `
    fragment SelectParticipantsContractFragment on PlanContract {
        id
        updatedAtUtc
        startDateLocal
        endDateLocal
        nextBillDate
        facility {
            shortName
        }
        enrollmentSpecifications {
            enrollmentType {
                id
                singular
                plural
            }
            count
            enrollments {
                customer {
                    id
                    commaName
                }
            }
        }
        promotions {
            id
            code
            fullCode
            source
            canRemove
            promotionCode {
                promotion {
                    translatedDescription(language: $language)
                }
            }
        }
    }
`)

export const SelectParticipantsPlanFragment = graphql(/** GraphQL */ `
    fragment SelectParticipantsPlanFragment on Plan {
        id
        planType
        ...PlanSessionJoinDatesPickerFragment
        sessions(facilityId: $facilityId) {
            id
            sessionFacilityHash
        }
        terms {
            enrollmentRanges {
                enrollmentType {
                    id
                    singular
                    plural
                }
                minQuantity
                maxQuantity
            }
            allowGuestCheckout
            billingInterval
            billingCycleString(language: $language)
        }
        bookingLimit(input: $input) {
            minimumEnrollments
            maximumEnrollments
        }
        terms {
            billingType
        }
    }
`)
</script>

<script setup lang="ts">
import ClientOnly from '#app/components/client-only'
import { useShowLoginModal, useViewer } from '~/composables/use-login'
import PrimeButton from 'primevue/button'
import { ref, computed, watch } from 'vue'
import { useActiveFacility, useShowAlert, useStorefront } from '#imports'
import {
    formatTimelessDate,
    isEndOfTime,
    parseLocal,
    parseTimeless,
    toLocalIsoString
} from '~/composables/support/localization'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import PrimeMessage from 'primevue/message'
import type {
    ChangeContractInput,
    CreateContractInput
} from '~/resources/graphql/graphql'
import EnrollmentTypeSelectors from '~/components/PurchaseContract/EnrollmentTypeSelectors.vue'
import PreviewContractPricingTable from '~/components/PurchaseContract/PreviewContractPricingTable.vue'
import SessionJoinDatesPicker from '~/components/PurchaseContract/SessionJoinDatesPicker.vue'
import { usePreviewSessionContractQuery } from '~/components/PurchaseContract/use-preview-session-contract-query'
import PurchaseContractSection from '~/components/PurchaseContract/PurchaseContractSection.vue'
import DuesPricingDisplay from '~/components/PurchaseContract/DuesPricingDisplay.vue'
import PromotionalCode from '~/components/PurchaseContract/PromotionalCode.vue'
import {
    type AppliedPromotionalCode,
    getForInput,
    hasSameAppliedPromotionalCodes
} from '~/components/PurchaseContract/promotional-code'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    plan: FragmentType<typeof SelectParticipantsPlanFragment>
    contract?: FragmentType<typeof SelectParticipantsContractFragment>
    isActiveSection: boolean
    isMutating: boolean
}>()

const emit = defineEmits<{
    (e: 'createContract', payload: CreateContractInput): void
    (e: 'changeContract', payload: ChangeContractInput): void
    (e: 'editSection'): void
}>()

const contractData = computed(() =>
    props.contract === undefined
        ? props.contract
        : getFragmentData(SelectParticipantsContractFragment, props.contract)
)

const planData = computed(() =>
    getFragmentData(SelectParticipantsPlanFragment, props.plan)
)

const activeFacility = useActiveFacility()
const { t } = useI18n({ useScope: 'global' })

const enrollmentInput = ref(
    planData.value.terms.enrollmentRanges.map(
        (range: {
            enrollmentType: { id: string; singular: string; plural: string }
            minQuantity: number
            maxQuantity: number
        }) => {
            return {
                enrollmentType: range.enrollmentType,
                count: range.minQuantity,
                minQuantity: range.minQuantity,
                maxQuantity: range.maxQuantity
            }
        }
    )
)

const enrollmentTypeCounts = computed(() => {
    return enrollmentInput.value
        .filter(element => element.count > 0)
        .map(element => {
            return {
                enrollmentTypeId: element.enrollmentType.id,
                count: element.count
            }
        })
})

// One session for memberships
const session = ref(planData.value.sessions[0])
const contractStartDate = ref<string | null>(null)
const contractEndDate = ref<string | null>(null)
const appliedPromotionalCodes = ref<AppliedPromotionalCode[]>([])

const totalEnrollmentCount = computed(() => {
    let totalEnrollmentCount = 0
    enrollmentInput.value.forEach(enrollmentType => {
        totalEnrollmentCount += enrollmentType.count
    })
    return totalEnrollmentCount
})

const continueAttempts = ref(0)
const bookingLimitsErrorMessage = computed(() => {
    if (
        planData.value.bookingLimit.minimumEnrollments !== null &&
        totalEnrollmentCount.value <
            planData.value.bookingLimit.minimumEnrollments
    ) {
        if (planData.value.planType === 'PASS') {
            return t(
                'purchaseContract.minimumParticipantsForPlan',
                {
                    planType: t('pass', {}, 1),
                    count: planData.value.bookingLimit.minimumEnrollments
                },
                planData.value.bookingLimit.minimumEnrollments
            )
        } else {
            return t(
                'purchaseContract.minimumParticipantsForPlan',
                {
                    planType: t('membership', {}, 1),
                    count: planData.value.bookingLimit.minimumEnrollments
                },
                planData.value.bookingLimit.minimumEnrollments
            )
        }
    }
    if (
        planData.value.bookingLimit.maximumEnrollments !== null &&
        totalEnrollmentCount.value >
            planData.value.bookingLimit.maximumEnrollments
    ) {
        return t(
            'purchaseContract.maximumParticipantsAllowed',
            {
                count: planData.value.bookingLimit.maximumEnrollments
            },
            planData.value.bookingLimit.maximumEnrollments
        )
    }

    return null
})

const startDateErrorMessage = computed(() =>
    contractStartDate.value === null
        ? t('purchaseContract.selectStartDate')
        : ''
)

const isRecurring = computed(
    () => planData.value.terms.billingType === 'RECURRING'
)

const {
    cart,
    duesPricing,
    promotions,
    nextBillDate,
    paymentDueMode,
    depositAmount,
    isFetching,
    subtotal,
    total
} = usePreviewSessionContractQuery(
    computed(() => planData.value.sessions[0].id),
    contractStartDate,
    enrollmentTypeCounts,
    appliedPromotionalCodes
)

const storefront = useStorefront()
const allowGuestCheckout = computed<boolean>(() => {
    if (planData.value.terms.billingType === 'RECURRING') {
        return (
            storefront.value.allowRecurringGuestCheckout &&
            planData.value.terms.allowGuestCheckout
        )
    }
    return (
        storefront.value.allowGuestCheckout &&
        planData.value.terms.allowGuestCheckout
    )
})

const showAlert = useShowAlert()
const viewer = useViewer()
const showLoginModal = useShowLoginModal()
async function handleContinue() {
    // The visible totals can belong to the previous preview while a new request is in
    // flight, so wait until pricing reflects the selected membership start date.
    if (isFetching.value) {
        return
    }

    continueAttempts.value += 1

    if (contractStartDate.value === null) {
        return showAlert(t('headsUp'), t('purchaseContract.selectStartDate'))
    }

    if (bookingLimitsErrorMessage.value !== null) {
        return
    }

    let customerId = viewer.value?.customerId
    if (customerId === undefined) {
        customerId = await showLoginModal({
            showGuest: allowGuestCheckout.value
        })
        if (customerId === undefined) {
            return
        }
    }

    if (subtotal.value === undefined || total.value === undefined) {
        return
    }

    emit('createContract', {
        customerId,
        sessionFacilityHash: session.value.sessionFacilityHash,
        startDate: contractStartDate.value,
        endDate: contractEndDate.value,
        promotionCodes: getForInput(appliedPromotionalCodes.value),
        // for the purpose of reserving slots
        enrollmentTypeCounts: enrollmentTypeCounts.value,
        subtotal: subtotal.value,
        total: total.value
    })
}

function handleSaveChanges() {
    if (contractData.value === undefined) {
        // We should on call save changes once we have an ID
        throw new Error()
    }

    // The previous preview remains visible while this request is in flight, so do not
    // submit its next billing date after the shopper has changed the membership start date.
    if (isFetching.value) {
        return
    }

    continueAttempts.value += 1

    if (contractStartDate.value === null) {
        showAlert(t('headsUp'), t('purchaseContract.selectStartDate'))
        return
    }

    if (bookingLimitsErrorMessage.value !== null) {
        return
    }

    if (subtotal.value === undefined || total.value === undefined) {
        return
    }

    emit('changeContract', {
        id: contractData.value.id,
        updatedAt: contractData.value.updatedAtUtc,
        // Rename startSessionFacilityHash
        startSessionFacilityHash: session.value.sessionFacilityHash,
        startDate: contractStartDate.value,
        endDate: contractEndDate.value,
        nextBillDate: isRecurring.value ? nextBillDate.value : undefined,
        promotionCodes: getForInput(appliedPromotionalCodes.value),
        // for the purpose of reserving slots
        enrollmentTypeCounts: enrollmentTypeCounts.value,
        subtotal: subtotal.value,
        total: total.value
    })
}

watch(
    promotions,
    newValue => {
        if (newValue === undefined || newValue === null) {
            return
        }

        const nextPromotions = newValue.map(promotion => {
            return {
                code: promotion.code,
                fullCode: promotion.fullCode,
                source: promotion.source,
                canRemove: promotion.canRemove,
                description:
                    promotion.promotionCode.promotion.translatedDescription ??
                    ''
            }
        })

        if (
            !hasSameAppliedPromotionalCodes(
                appliedPromotionalCodes.value,
                nextPromotions
            )
        ) {
            // Keep the promo-code list in sync with the preview response so the
            // eventual create/change mutation submits the same effective set
            // that pricing was calculated from.
            appliedPromotionalCodes.value = nextPromotions
        }
    },
    { immediate: true }
)

watch(
    contractData,
    newValue => {
        if (newValue === undefined) {
            return
        }

        enrollmentInput.value = enrollmentInput.value.map(input => {
            const contractInput = newValue.enrollmentSpecifications.find(
                spec => spec.enrollmentType.id === input.enrollmentType.id
            )

            if (contractInput === undefined) {
                return { ...input }
            }

            return {
                ...input,
                count: contractInput.count
            }
        })

        contractStartDate.value = toLocalIsoString(
            parseLocal(newValue.startDateLocal)
        )

        appliedPromotionalCodes.value = newValue.promotions.map(promotion => ({
            contractPromotionId: promotion.id,
            code: promotion.code,
            fullCode: promotion.fullCode,
            source: promotion.source,
            canRemove: promotion.canRemove,
            description:
                promotion.promotionCode?.promotion.translatedDescription ?? ''
        }))
    },
    { immediate: true }
)

const formattedEndDate = computed(() => {
    if (contractData.value === undefined) {
        return null
    }

    const endDate = parseLocal(contractData.value.endDateLocal)
    if (isEndOfTime(endDate)) {
        return null
    }

    return formatTimelessDate(endDate)
})

const formattedNextBillDate = computed(() => {
    if (contractData.value === undefined) {
        return null
    }

    if (contractData.value.nextBillDate === null) {
        return null
    }

    const endDate = parseTimeless(contractData.value.nextBillDate)
    return formatTimelessDate(endDate)
})
</script>
